import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from './api.js';

const AuthContext = createContext(null);

// Which company the portal is working on, for people in more than one (e.g. after
// accepting a colleague's invite). Kept in this browser only.
const ORG_KEY = 'ss_org';
const storedOrg = () => { try { return Number(localStorage.getItem(ORG_KEY)) || null; } catch { return null; } };
export const rememberOrganisation = (id) => { try { localStorage.setItem(ORG_KEY, String(id)); } catch { /* private mode */ } };

// A team invite being accepted through sign-up (the token rides along to the code step)
const INVITE_KEY = 'ss_invite';
export const pendingInvite = {
  get: () => { try { return JSON.parse(sessionStorage.getItem(INVITE_KEY) || 'null'); } catch { return null; } },
  set: (v) => { try { sessionStorage.setItem(INVITE_KEY, JSON.stringify(v)); } catch { /* private mode */ } },
  clear: () => { try { sessionStorage.removeItem(INVITE_KEY); } catch { /* private mode */ } },
};

const SIGNED_OUT = { loading: false, user: null, roles: [], isAdmin: false, organisations: [], organisation: null, suggestedEmailDomain: null };

export function AuthProvider({ children }) {
  const [state, setState] = useState({ ...SIGNED_OUT, loading: true });

  /** Reloads who is signed in; returns the new state (so callers can route by role). */
  const refresh = useCallback(async () => {
    let next;
    try {
      const me = await api('/portal/auth/me');
      next = {
        loading: false, user: me.user, roles: me.roles || [], isAdmin: !!me.is_admin,
        organisations: me.organisations || [],
        // The company the portal is working on: the one picked last, else the first
        organisation: (me.organisations || []).find((o) => o.id === storedOrg()) || me.organisations?.[0] || null,
        suggestedEmailDomain: me.suggested_email_domain || null,
      };
    } catch {
      next = SIGNED_OUT;
    }
    setState(next);
    return next;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /** Throws ApiError; code 'email_not_verified' carries the verification details in err.data. */
  const login = async (email, password) => {
    await api('/portal/auth/login', { method: 'POST', body: { email, password } });
    return refresh();
  };

  /** Creates the (unverified) account and emails a code. No session yet. */
  const register = (fields) => api('/portal/auth/register', { method: 'POST', body: fields });

  /** Confirms the email; the API starts the session (and joins an invited company). */
  const verifyEmail = async (email, code) => {
    const invite = pendingInvite.get();
    const inviteToken = invite && invite.email === String(email).trim().toLowerCase() ? invite.token : undefined;
    const res = await api('/portal/auth/verify-email', { method: 'POST', body: { email, code, invite_token: inviteToken } });
    pendingInvite.clear();
    if (res?.joined_organisation_id) rememberOrganisation(res.joined_organisation_id);
    return refresh();
  };

  /** Switch the company the portal is working on. */
  const selectOrganisation = (id) => {
    rememberOrganisation(id);
    setState((st) => ({ ...st, organisation: st.organisations.find((o) => o.id === id) || st.organisation }));
  };

  const resendCode = (email) => api('/portal/auth/resend-code', { method: 'POST', body: { email } });

  /** Always succeeds: the API never says whether the address has an account. */
  const forgotPassword = (email) => api('/portal/auth/forgot-password', { method: 'POST', body: { email } });

  /** Code + new password → signed in. Returns the new state. */
  const resetPassword = async (fields) => {
    await api('/portal/auth/reset-password', { method: 'POST', body: fields });
    return refresh();
  };

  const logout = async () => {
    await api('/portal/auth/logout', { method: 'POST' });
    setState(SIGNED_OUT);
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, verifyEmail, selectOrganisation, resendCode, forgotPassword, resetPassword, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function RequireAuth({ children }) {
  const { loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <div className="page-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/**
 * Where someone lands after signing in:
 *   platform admins → /admin
 *   a company that hasn't done "Your company" yet → /onboarding
 *   an owner/admin whose company hasn't finished (or skipped) the first-run guide → /setup
 *   anyone with a company → its dashboard
 */
export function homeFor(state) {
  if (state?.isAdmin) return '/admin';
  if (state?.organisation && !state.organisation.onboarded) return '/onboarding';
  if (state?.organisation && !state.organisation.setup_done && ['owner', 'admin'].includes(state.organisation.role)) return '/setup';
  if (state?.organisation) return '/dashboard';
  return '/company';
}

/** Customer pages: send a company that hasn't set itself up to "Your company" first. */
export function RequireOnboarded({ children }) {
  const { organisation } = useAuth();
  if (organisation && !organisation.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

/** Platform admins only. Anyone else is sent to their own home, never shown the page. */
export function RequireAdmin({ children }) {
  const { loading, user, isAdmin } = useAuth();
  const location = useLocation();
  if (loading) return <div className="page-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!isAdmin) return <Navigate to="/events" replace />;
  return children;
}

/** "/" → the right home for whoever is signed in. */
export function Home() {
  const auth = useAuth();
  if (auth.loading) return <div className="page-loading">Loading…</div>;
  return <Navigate to={auth.user ? homeFor(auth) : '/login'} replace />;
}
